/**
 * PowerInventoryPage - Main page for viewing and managing power inventory
 * Allows agents to view unexecuted power counts and reassign powers between any two agencies
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { ArchiveBoxIcon, XMarkIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import {
  OathPageWrapper,
  OathHeaderCard,
  OathContentCard,
  OathButton,
  OathAlert,
  OathSpinner,
} from '../../shared/OathUI';
import { useOathAuth } from '../../../services/oathAuthContext';
import {
  getInventorySummary,
  getUnexecutedPowers,
  reassignPowers,
} from '../../../services/oathPowerInventoryService';
import InventorySummaryPanel from './components/InventorySummaryPanel';
import PowerGrid from './components/PowerGrid';
import TransferControls from './components/TransferControls';
import SaveConfirmationModal from './components/SaveConfirmationModal';

/**
 * Dismissible alert wrapper since OathAlert doesn't support onDismiss
 */
const DismissibleAlert = ({ variant, message, onDismiss }) => (
  <div className="relative mb-4">
    <OathAlert variant={variant} message={message} />
    <button
      onClick={onDismiss}
      className="absolute top-2 right-2 p-1 rounded-md hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
      aria-label="Dismiss alert"
    >
      <XMarkIcon className="h-4 w-4" />
    </button>
  </div>
);

/**
 * Custom dropdown for agency selection with power count badges
 */
const AgencyDropdown = ({ id, value, onChange, agencies, powerCounts, placeholder = 'Select agency...' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);
  const [dropdownStyle, setDropdownStyle] = useState({});

  const selectedAgency = agencies.find((a) => a.agencyId === value);

  const toggleOpen = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownMaxHeight = 240;
      const gap = 4;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      const openUpward = spaceBelow < dropdownMaxHeight + gap && spaceAbove > spaceBelow;

      if (openUpward) {
        setDropdownStyle({
          position: 'fixed',
          bottom: viewportHeight - rect.top + gap,
          left: rect.left,
          width: Math.max(rect.width, 280),
          maxHeight: Math.min(dropdownMaxHeight, spaceAbove - gap),
        });
      } else {
        setDropdownStyle({
          position: 'fixed',
          top: rect.bottom + gap,
          left: rect.left,
          width: Math.max(rect.width, 280),
          maxHeight: Math.min(dropdownMaxHeight, spaceBelow - gap),
        });
      }
    }
    setIsOpen(!isOpen);
  };

  const handleSelect = (agencyId) => {
    onChange(agencyId);
    setIsOpen(false);
  };

  const selectedCount = powerCounts.get(value) ?? 0;

  return (
    <div className="relative" data-testid={`dropdown-${id}`}>
      <button
        ref={buttonRef}
        id={id}
        type="button"
        onClick={toggleOpen}
        className={`w-full sm:w-auto sm:max-w-[320px] min-w-[200px] px-3 py-1.5 text-sm border rounded-xl text-left bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-200 flex items-center justify-between gap-2 ${
          'border-gray-300 dark:border-gray-600'
        } ${!selectedAgency ? 'text-gray-400 dark:text-gray-500' : ''}`}
      >
        {selectedAgency ? (
          <span className="flex items-center gap-1 min-w-0">
            <span className="truncate">{selectedAgency.dbaAgencyName || selectedAgency.agencyName}</span>
            <span className="flex-shrink-0 text-gray-400 dark:text-gray-500">({selectedAgency.agencyId})</span>
          </span>
        ) : (
          <span className="truncate min-w-0">{placeholder}</span>
        )}
        <span className="flex items-center gap-1.5 flex-shrink-0">
          {selectedAgency && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-primary-50 text-primary dark:bg-primary-900/40 dark:text-primary-300">
              {selectedCount}
            </span>
          )}
          <ChevronDownIcon className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </span>
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div
            className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl shadow-lg z-50 overflow-auto"
            style={dropdownStyle}
          >
            <button
              type="button"
              onClick={() => handleSelect(null)}
              className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-600 ${
                !value
                  ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-dark dark:text-primary-light font-medium'
                  : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              {placeholder}
            </button>
            {agencies.map((a) => {
              const count = powerCounts.get(a.agencyId) ?? 0;
              const isSelected = a.agencyId === value;
              return (
                <button
                  key={a.agencyId}
                  type="button"
                  onClick={() => handleSelect(a.agencyId)}
                  className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-600 flex items-center justify-between ${
                    isSelected
                      ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-dark dark:text-primary-light font-medium'
                      : 'text-gray-700 dark:text-gray-200'
                  }`}
                >
                  <span className="flex items-center gap-1 min-w-0">
                    <span className="truncate">{a.dbaAgencyName || a.agencyName}</span>
                    <span className="flex-shrink-0 text-gray-400 dark:text-gray-500">({a.agencyId})</span>
                  </span>
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ml-2 ${
                    isSelected
                      ? 'bg-primary-100 text-primary-dark dark:bg-primary-800/50 dark:text-primary-300'
                      : 'bg-gray-100 text-gray-600 dark:bg-gray-600 dark:text-gray-300'
                  }`}>
                    {count} powers
                  </span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

const PowerInventoryPage = () => {
  const { user } = useOathAuth();
  const agencies = useMemo(() => user?.agencies || [], [user?.agencies]);

  // Agency selection — two dropdowns, both from user.agencies
  const [selectedAgencyId1, setSelectedAgencyId1] = useState(null);
  const [selectedAgencyId2, setSelectedAgencyId2] = useState(null);

  // Inventory summary (loaded for agency 1)
  const [inventorySummary, setInventorySummary] = useState(null);
  const [activeTab, setActiveTab] = useState('S');

  // Power grids (working copies for local moves)
  const [agency1Powers, setAgency1Powers] = useState([]);
  const [agency2Powers, setAgency2Powers] = useState([]);

  // Original snapshots for reset (defensive copies)
  const original1Ref = useRef([]);
  const original2Ref = useRef([]);

  // Selection state
  const [selected1Ids, setSelected1Ids] = useState(new Set());
  const [selected2Ids, setSelected2Ids] = useState(new Set());

  // Range inputs
  const [startPowerNo, setStartPowerNo] = useState('');
  const [endPowerNo, setEndPowerNo] = useState('');

  // UI state
  const [loading, setLoading] = useState(false);
  const [powersLoading, setPowersLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);

  // Set initial agency 1
  useEffect(() => {
    if (agencies.length > 0 && !selectedAgencyId1) {
      const mainAgency = agencies.find(a => a.isMainAgency);
      const canPostAgency = agencies.find(a => a.canPost);
      const defaultAgency = mainAgency || canPostAgency || agencies[0];
      setSelectedAgencyId1(defaultAgency.agencyId);
    }
  }, [agencies, selectedAgencyId1]);

  // Load summary when agency 1 changes (with cancellation)
  useEffect(() => {
    if (!selectedAgencyId1) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);
      setAgency1Powers([]);
      setAgency2Powers([]);
      setSelected1Ids(new Set());
      setSelected2Ids(new Set());

      try {
        const summaryResult = await getInventorySummary(selectedAgencyId1);
        if (cancelled) return;

        if (summaryResult.success) {
          setInventorySummary(summaryResult.data);
        } else {
          setError(summaryResult.error || 'Failed to load inventory summary');
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [selectedAgencyId1]);

  // Load unexecuted powers when both agencies are selected (with cancellation)
  useEffect(() => {
    if (!selectedAgencyId1 || !selectedAgencyId2) return;
    let cancelled = false;

    const load = async () => {
      setPowersLoading(true);
      setSelected1Ids(new Set());
      setSelected2Ids(new Set());
      setSuccessMessage(null);

      try {
        const [result1, result2] = await Promise.all([
          getUnexecutedPowers(selectedAgencyId1),
          getUnexecutedPowers(selectedAgencyId2),
        ]);
        if (cancelled) return;

        const data1 = result1.success ? (result1.data || []) : [];
        const data2 = result2.success ? (result2.data || []) : [];

        setAgency1Powers(data1);
        setAgency2Powers(data2);
        original1Ref.current = [...data1];
        original2Ref.current = [...data2];
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setPowersLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [selectedAgencyId1, selectedAgencyId2]);

  // Transfer powers between grids (local state only)
  const handleTransferToAgency2 = useCallback(() => {
    if (selected1Ids.size === 0) return;
    setSuccessMessage(null);
    const toMove = agency1Powers.filter((p) => selected1Ids.has(p.powerNo));
    setAgency1Powers((prev) => prev.filter((p) => !selected1Ids.has(p.powerNo)));
    setAgency2Powers((prev) =>
      [...prev, ...toMove].sort((a, b) => a.powerNo.localeCompare(b.powerNo, undefined, { numeric: true }))
    );
    setSelected1Ids(new Set());
  }, [agency1Powers, selected1Ids]);

  const handleTransferToAgency1 = useCallback(() => {
    if (selected2Ids.size === 0) return;
    setSuccessMessage(null);
    const toMove = agency2Powers.filter((p) => selected2Ids.has(p.powerNo));
    setAgency2Powers((prev) => prev.filter((p) => !selected2Ids.has(p.powerNo)));
    setAgency1Powers((prev) =>
      [...prev, ...toMove].sort((a, b) => a.powerNo.localeCompare(b.powerNo, undefined, { numeric: true }))
    );
    setSelected2Ids(new Set());
  }, [agency2Powers, selected2Ids]);

  // Undo a single pending move — restore power to its original grid
  const handleUndoMove = useCallback((powerNo) => {
    const original1Nos = new Set(original1Ref.current.map((p) => p.powerNo));
    if (original1Nos.has(powerNo)) {
      // Was in agency1 originally, currently in agency2 → move back to agency1
      const power = agency2Powers.find((p) => p.powerNo === powerNo);
      if (!power) return;
      setAgency2Powers((prev) => prev.filter((p) => p.powerNo !== powerNo));
      setAgency1Powers((prev) =>
        [...prev, power].sort((a, b) => a.powerNo.localeCompare(b.powerNo, undefined, { numeric: true }))
      );
    } else {
      // Was in agency2 originally, currently in agency1 → move back to agency2
      const power = agency1Powers.find((p) => p.powerNo === powerNo);
      if (!power) return;
      setAgency1Powers((prev) => prev.filter((p) => p.powerNo !== powerNo));
      setAgency2Powers((prev) =>
        [...prev, power].sort((a, b) => a.powerNo.localeCompare(b.powerNo, undefined, { numeric: true }))
      );
    }
    setSuccessMessage(null);
  }, [agency1Powers, agency2Powers]);

  // Quick-add a single power by number to agency 2
  const handleQuickAddToAgency2 = useCallback((powerNo) => {
    const power = agency1Powers.find((p) => p.powerNo === powerNo);
    if (!power) return;
    setSuccessMessage(null);
    setAgency1Powers((prev) => prev.filter((p) => p.powerNo !== powerNo));
    setAgency2Powers((prev) =>
      [...prev, power].sort((a, b) => a.powerNo.localeCompare(b.powerNo, undefined, { numeric: true }))
    );
    setSelected1Ids((prev) => { const next = new Set(prev); next.delete(powerNo); return next; });
  }, [agency1Powers]);

  // Quick-add a single power by number to agency 1
  const handleQuickAddToAgency1 = useCallback((powerNo) => {
    const power = agency2Powers.find((p) => p.powerNo === powerNo);
    if (!power) return;
    setSuccessMessage(null);
    setAgency2Powers((prev) => prev.filter((p) => p.powerNo !== powerNo));
    setAgency1Powers((prev) =>
      [...prev, power].sort((a, b) => a.powerNo.localeCompare(b.powerNo, undefined, { numeric: true }))
    );
    setSelected2Ids((prev) => { const next = new Set(prev); next.delete(powerNo); return next; });
  }, [agency2Powers]);

  // Select range across both grids
  const handleSelectRange = useCallback(() => {
    if (!startPowerNo && !endPowerNo) return;
    const startNum = startPowerNo ? parseInt(startPowerNo, 10) : -Infinity;
    const endNum = endPowerNo ? parseInt(endPowerNo, 10) : Infinity;

    const newSelection1 = new Set();
    agency1Powers.forEach((p) => {
      const pNum = parseInt(p.powerNo, 10);
      if (!isNaN(pNum) && pNum >= startNum && pNum <= endNum) {
        newSelection1.add(p.powerNo);
      }
    });
    setSelected1Ids(newSelection1);

    const newSelection2 = new Set();
    agency2Powers.forEach((p) => {
      const pNum = parseInt(p.powerNo, 10);
      if (!isNaN(pNum) && pNum >= startNum && pNum <= endNum) {
        newSelection2.add(p.powerNo);
      }
    });
    setSelected2Ids(newSelection2);
  }, [startPowerNo, endPowerNo, agency1Powers, agency2Powers]);

  // Memoized change detection
  const hasUnsavedChanges = useMemo(() => {
    const original1Nos = new Set(original1Ref.current.map((p) => p.powerNo));
    const current1Nos = new Set(agency1Powers.map((p) => p.powerNo));
    if (original1Nos.size !== current1Nos.size) return true;
    for (const no of original1Nos) {
      if (!current1Nos.has(no)) return true;
    }
    return false;
  }, [agency1Powers]);

  // Pending queue — reactive diff between original refs and current state
  const pendingQueue = useMemo(() => {
    const original1Nos = new Set(original1Ref.current.map((p) => p.powerNo));
    const original2Nos = new Set(original2Ref.current.map((p) => p.powerNo));
    const current1Nos = new Set(agency1Powers.map((p) => p.powerNo));
    const current2Nos = new Set(agency2Powers.map((p) => p.powerNo));
    return {
      movedToAgency2: [...original1Nos].filter((no) => current2Nos.has(no) && !original2Nos.has(no)),
      movedToAgency1: [...original2Nos].filter((no) => current1Nos.has(no) && !original1Nos.has(no)),
    };
  }, [agency1Powers, agency2Powers]);

  const pendingCount = pendingQueue.movedToAgency2.length + pendingQueue.movedToAgency1.length;

  // Save click opens the confirmation modal
  const handleSaveClick = () => {
    setShowSaveConfirmation(true);
  };

  // Confirm & Save from the modal
  const handleConfirmSave = () => {
    setShowSaveConfirmation(false);
    handleSave();
  };

  // Compute diffs and save
  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const { movedToAgency2: movedTo2, movedToAgency1: movedTo1 } = pendingQueue;

      const promises = [];

      if (movedTo2.length > 0) {
        promises.push(
          reassignPowers({
            sourceAgencyId: selectedAgencyId1,
            destinationAgencyId: selectedAgencyId2,
            powerNumbers: movedTo2,
          })
        );
      }

      if (movedTo1.length > 0) {
        promises.push(
          reassignPowers({
            sourceAgencyId: selectedAgencyId2,
            destinationAgencyId: selectedAgencyId1,
            powerNumbers: movedTo1,
          })
        );
      }

      if (promises.length === 0) {
        setSuccessMessage('No changes to save.');
        setSaving(false);
        return;
      }

      const results = await Promise.allSettled(promises);
      const fulfilled = results.filter((r) => r.status === 'fulfilled').map((r) => r.value);
      const rejected = results.filter((r) => r.status === 'rejected').map((r) => r.reason);

      const apiErrors = fulfilled.filter((r) => !r.success);
      const successResults = fulfilled.filter((r) => r.success);
      const totalReassigned = successResults.reduce((sum, r) => sum + (r.data?.reassignedCount || 0), 0);

      const allErrors = [
        ...rejected.map((e) => e.message || 'Network error'),
        ...apiErrors.map((e) => e.error || 'API error'),
      ];

      if (allErrors.length > 0 && totalReassigned > 0) {
        const msg = `Partially completed: ${totalReassigned} power(s) reassigned. Errors: ${allErrors.join('; ')}`;
        setError(msg);
        toast.warning(`Partially completed: ${totalReassigned} power(s) reassigned with some errors.`);
      } else if (allErrors.length > 0) {
        setError(allErrors.join('; '));
      } else {
        const msg = `Successfully reassigned ${totalReassigned} power(s).`;
        setSuccessMessage(msg);
        toast.success(msg);
      }

      // Reload summary for agency 1
      const summaryResult = await getInventorySummary(selectedAgencyId1);
      if (summaryResult.success) {
        setInventorySummary(summaryResult.data);
      }

      // Reload powers for both grids
      if (selectedAgencyId2) {
        const [result1, result2] = await Promise.all([
          getUnexecutedPowers(selectedAgencyId1),
          getUnexecutedPowers(selectedAgencyId2),
        ]);
        const data1 = result1.success ? (result1.data || []) : [];
        const data2 = result2.success ? (result2.data || []) : [];
        setAgency1Powers(data1);
        setAgency2Powers(data2);
        original1Ref.current = [...data1];
        original2Ref.current = [...data2];
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Reset to original state
  const handleReset = () => {
    setAgency1Powers([...original1Ref.current]);
    setAgency2Powers([...original2Ref.current]);
    setSelected1Ids(new Set());
    setSelected2Ids(new Set());
    setStartPowerNo('');
    setEndPowerNo('');
    setSuccessMessage(null);
  };

  // Handle agency 1 change — clear agency 2 if same
  const handleAgency1Change = (agencyId) => {
    setSelectedAgencyId1(agencyId);
    if (agencyId && agencyId === selectedAgencyId2) {
      setSelectedAgencyId2(null);
    }
  };

  // Handle agency 2 change
  const handleAgency2Change = (agencyId) => {
    setSelectedAgencyId2(agencyId);
  };

  // Derive power counts per agency from inventory summary
  const agencyPowerCounts = useMemo(() => {
    const counts = new Map();
    if (!inventorySummary) return counts;
    if (inventorySummary.mainAgency) {
      const total = (inventorySummary.mainAgency.inventory || []).reduce((sum, i) => sum + (i.count || 0), 0);
      counts.set(inventorySummary.mainAgency.agencyId, total);
    }
    (inventorySummary.subAgencies || []).forEach((sub) => {
      const total = (sub.inventory || []).reduce((sum, i) => sum + (i.count || 0), 0);
      counts.set(sub.agencyId, total);
    });
    return counts;
  }, [inventorySummary]);

  const agency1Name = agencies.find((a) => a.agencyId === selectedAgencyId1)?.dbaAgencyName || 'Agency 1';
  const agency2Name = agencies.find((a) => a.agencyId === selectedAgencyId2)?.dbaAgencyName || 'Agency 2';

  const selectedSubAgencySummary = inventorySummary?.subAgencies?.find(
    (s) => s.agencyId === selectedAgencyId2
  );

  return (
    <>
    <OathPageWrapper maxWidth="max-w-7xl">
      {/* Header */}
      <OathHeaderCard
        title="Power Inventory"
        subtitle="View and manage unexecuted power inventory across your agencies"
        icon={ArchiveBoxIcon}
      />

      {/* Alerts */}
      {error && (
        <DismissibleAlert variant="error" message={error} onDismiss={() => setError(null)} />
      )}
      {successMessage && (
        <DismissibleAlert variant="success" message={successMessage} onDismiss={() => setSuccessMessage(null)} />
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <OathSpinner size="lg" />
        </div>
      ) : !inventorySummary && agencies.length === 0 ? (
        <OathContentCard>
          <p className="text-center text-gray-500 dark:text-gray-400 py-8">
            No agencies found. Contact your administrator for access.
          </p>
        </OathContentCard>
      ) : inventorySummary ? (
        <>
          {/* Inventory Summary */}
          <OathContentCard className="mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Inventory Summary
              </h2>
              <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 sm:gap-4">
                {/* Agency 1 dropdown */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                  <label htmlFor="agency-1-select" className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                    Agency
                  </label>
                  <AgencyDropdown
                    id="agency-1-select"
                    value={selectedAgencyId1}
                    onChange={handleAgency1Change}
                    agencies={agencies}
                    powerCounts={agencyPowerCounts}
                  />
                </div>
                {/* Agency 2 dropdown */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                  <label htmlFor="agency-2-select" className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                    Agency
                  </label>
                  <AgencyDropdown
                    id="agency-2-select"
                    value={selectedAgencyId2}
                    onChange={handleAgency2Change}
                    agencies={agencies.filter((a) => a.agencyId !== selectedAgencyId1)}
                    powerCounts={agencyPowerCounts}
                  />
                </div>
              </div>
            </div>

            <InventorySummaryPanel
              mainAgency={inventorySummary.mainAgency}
              selectedSubAgency={selectedSubAgencySummary}
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />
          </OathContentCard>

          {/* Power Reassignment - shown when both agencies selected */}
          {selectedAgencyId1 && selectedAgencyId2 && (
            <OathContentCard>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Power Reassignment
              </h2>

              <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-4">
                {/* Agency 1 powers */}
                <PowerGrid
                  powers={agency1Powers}
                  selectedIds={selected1Ids}
                  onSelectionChange={setSelected1Ids}
                  title={`${agency1Name} Powers`}
                  emptyMessage="No unexecuted powers"
                  loading={powersLoading}
                  onQuickAdd={handleQuickAddToAgency2}
                  quickAddPlaceholder="Add power #..."
                />

                {/* Transfer controls — shown first on mobile, center column on desktop */}
                <div className="order-first lg:order-none">
                  <TransferControls
                    onTransferToSub={handleTransferToAgency2}
                    onTransferToMain={handleTransferToAgency1}
                    startPowerNo={startPowerNo}
                    endPowerNo={endPowerNo}
                    onStartChange={setStartPowerNo}
                    onEndChange={setEndPowerNo}
                    onSelectRange={handleSelectRange}
                    canTransferToSub={selected1Ids.size > 0}
                    canTransferToMain={selected2Ids.size > 0}
                    agency1Name={agency1Name}
                    agency2Name={agency2Name}
                    pendingQueue={pendingQueue}
                    onUndoMove={handleUndoMove}
                    selectedCount1={selected1Ids.size}
                    selectedCount2={selected2Ids.size}
                  />
                </div>

                {/* Agency 2 powers */}
                <PowerGrid
                  powers={agency2Powers}
                  selectedIds={selected2Ids}
                  onSelectionChange={setSelected2Ids}
                  title={`${agency2Name} Powers`}
                  emptyMessage="No unexecuted powers"
                  loading={powersLoading}
                  onQuickAdd={handleQuickAddToAgency1}
                  quickAddPlaceholder="Add power #..."
                />
              </div>

            </OathContentCard>
          )}
          {hasUnsavedChanges && selectedAgencyId1 && selectedAgencyId2 && (
            <div className="h-16" aria-hidden="true" />
          )}
        </>
      ) : null}
    </OathPageWrapper>

    {/* Sticky save bar — outside OathPageWrapper to avoid overflow-hidden clipping */}
    {selectedAgencyId1 && selectedAgencyId2 && hasUnsavedChanges && (
      <div className="fixed inset-x-0 bottom-0 z-40 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 shadow-[0_-4px_12px_rgba(0,0,0,0.08)] animate-slide-up">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex flex-col sm:flex-row items-center justify-between gap-2 sm:gap-0">
          <div className="flex items-center gap-2">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              You have unsaved changes
            </p>
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-primary-50 text-primary dark:bg-primary-900/40 dark:text-primary-300">
              {pendingCount}
            </span>
          </div>
          <div className="flex gap-3 w-full sm:w-auto">
            <OathButton variant="outline" onClick={handleReset} disabled={saving} className="flex-1 sm:flex-initial">
              Reset
            </OathButton>
            <OathButton variant="primary" onClick={handleSaveClick} disabled={saving} loading={saving} className="flex-1 sm:flex-initial">
              Save Changes
            </OathButton>
          </div>
        </div>
      </div>
    )}

    {/* Save confirmation modal */}
    <SaveConfirmationModal
      isOpen={showSaveConfirmation}
      onClose={() => setShowSaveConfirmation(false)}
      onConfirm={handleConfirmSave}
      saving={saving}
      pendingQueue={pendingQueue}
      agency1Name={agency1Name}
      agency2Name={agency2Name}
    />
    </>
  );
};

export default PowerInventoryPage;
